import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { SessionFilter, getBotDeploymentIdsByBotIdSelector } from '@common/reducers/ChatbotSelectors';
import { AutomationSessionReducerActions, DBAutomationSessionThunks, automationActionsActions, automationSessionSelectByQuery } from '@common/reducers/DBServiceThunks';
import { useBulkDeleteEntitiesMutation, useCalculateBotStatsMutation } from '@common/reducers/backend-api/backend-api';
import { ChevronLeft, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SessionFilters } from './SessionFilters';
import { SessionRow } from './SessionRow';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

export interface SessionsListProps {
    filter: SessionFilter;
    onSessionFilterChange: (filter: SessionFilter) => void;
    selectedDeploymentId: string | null;
    onDeploymentSelect: (deploymentId: string | null) => void;
}

const EmptyState = ({ title, message }: { title: string; message: string }) => (
    <div className="flex flex-col items-center justify-center h-full w-full flex-1 text-center p-8">
        <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            marginBottom: '8px',
            color: 'var(--sessions-list-empty-title)',
        }}>
            {title}
        </h3>
        <p style={{
            fontSize: '0.875rem',
            color: 'var(--sessions-list-empty-text)',
        }}>
            {message}
        </p>
    </div>
);

// Pagination settings
const SESSIONS_PER_PAGE = 25;

export const SessionsList = ({ 
    filter, 
    onSessionFilterChange, 
    selectedDeploymentId, 
    onDeploymentSelect
}: SessionsListProps) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const { botId } = useParams<{ botId: string }>();
    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
    const [bulkDeleteEntities, { isLoading: isBulkDeleting }] = useBulkDeleteEntitiesMutation();
    const [calculateBotStats] = useCalculateBotStatsMutation();

    // Create a ref for the intersection observer target
    const observerTarget = useRef<HTMLDivElement>(null);

    // Get loading state and pagination info from Redux
    const { 
        itemsLoading: isLoading, 
        itemsLoaded,
        total: totalSessions,
        skip,
        limit 
    } = useAppSelector(state => state.DBAutomationSessionReducer);
    
    // Calculate current page from skip and limit
    const currentPage = Math.floor(skip / limit) + 1;
    const totalPages = Math.ceil(totalSessions / limit);
    
    const deploymentIds = useAppSelector(state => 
        botId ? getBotDeploymentIdsByBotIdSelector(state, botId) : []
    );

    // Get sessions directly from the store using the selector
    const sessions = useAppSelector(state => {
        const query: any = {};
        
        // Apply filter based on status if needed
        if (filter === 'active') {
            query.status = { $ne: 'ENDED' };
        } else if (filter === 'inactive') {
            query.status = 'ENDED';
        }
        
        // Apply filter for botId
        if (botId) {
            query.botId = botId;
        }
        
        // Apply filter for deploymentId if selected
        if (selectedDeploymentId) {
            query.deploymentId = selectedDeploymentId;
        }
        
        return automationSessionSelectByQuery(state.DBAutomationSessionReducer, query);
    });
    
    console.log('Redux pagination status:', { 
        currentPage, 
        totalPages, 
        totalSessions, 
        skip, 
        limit,
        isLoading,
        itemsLoaded,
        sessionsCount: sessions.length
    });

    // Function to fetch sessions with the correct query
    const fetchSessions = useCallback((page: number, isInfiniteScroll = false) => {
        if (!botId) return;
        
        // Clear existing sessions if not infinite scrolling
        if (!isInfiniteScroll || page === 1) {
            dispatch(AutomationSessionReducerActions.clearState());
        }
        
        // Set up query parameters
        const query: any = {
            botId: botId,
            $limit: SESSIONS_PER_PAGE,
            $skip: (page - 1) * SESSIONS_PER_PAGE,
            $sort: { updatedAt: -1 } // Most recent first
        };
        
        // Apply filter based on status if needed
        if (filter === 'active') {
            query.status = { $ne: 'ENDED' };
        } else if (filter === 'inactive') {
            query.status = 'ENDED';
        }
        
        // Apply filter for deploymentId if selected
        if (selectedDeploymentId) {
            query.deploymentId = selectedDeploymentId;
        }
        
        console.log('Fetching sessions with query:', query);

        dispatch(AutomationSessionReducerActions.clearState());
        
        dispatch(DBAutomationSessionThunks.find(query))
            .unwrap()
            .then(result => {
                console.log('Sessions fetch result:', result);
            })
            .catch(error => {
                console.error('Error fetching sessions:', error);
            });
    }, [botId, filter, selectedDeploymentId, dispatch]);

    // Initial fetch and refetch when dependencies change
    useEffect(() => {
        console.log('Filter or deployment changed, fetching sessions', {
            botId,
            filter,
            selectedDeploymentId
        });
        fetchSessions(1);
    }, [botId, filter, selectedDeploymentId, fetchSessions]);
    
    // Handle page changes
    const handlePageChange = (newPage: number) => {
        const isInfiniteScroll = newPage > currentPage;
        fetchSessions(newPage, isInfiniteScroll);
    };
    
    // Handlers for pagination buttons
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    const handleDeleteAllConfirm = async () => {
        if (!botId) return;

        const query: Record<string, any> = { botId };
        if (filter === 'active') {
            query.status = { $ne: 'ENDED' };
        } else if (filter === 'inactive') {
            query.status = 'ENDED';
        }
        if (selectedDeploymentId) {
            query.deploymentId = selectedDeploymentId;
        }

        try {
            await bulkDeleteEntities({ service: 'automation-session', query }).unwrap();
            setDeleteAllDialogOpen(false);
            dispatch(AutomationSessionReducerActions.clearState());
            fetchSessions(1);
            calculateBotStats([botId]);
        } catch (error) {
            console.error('Bulk delete failed:', error);
        }
    };

    const getEmptyMessage = () => {
        if (selectedDeploymentId) {
            if (filter === 'active') return t('chatbot.botDetail.sessions.empty.activeForVersion');
            if (filter === 'inactive') return t('chatbot.botDetail.sessions.empty.inactiveForVersion');
            return t('chatbot.botDetail.sessions.empty.allForVersion');
        }

        if (filter === 'active') return t('chatbot.botDetail.sessions.empty.active');
        if (filter === 'inactive') return t('chatbot.botDetail.sessions.empty.inactive');
        return t('chatbot.botDetail.sessions.empty.all');
    };

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pb-3 gap-4 border-b border-gray-200 dark:border-slate-700 flex flex-row flex-start items-center">
                <h5 className="text-base font-semibold text-gray-900 dark:text-slate-100">
                    {t('chatbot.botDetail.sessions.title')}
                </h5>
                <div>
                    <SessionFilters
                        sessionFilter={filter}
                        onSessionFilterChange={onSessionFilterChange}
                        selectedDeploymentId={selectedDeploymentId}
                        onDeploymentSelect={onDeploymentSelect}
                    />
                </div>
                {totalSessions > 0 && (
                    <Button
                        onClick={() => setDeleteAllDialogOpen(true)}
                        variant="destructive"
                        size="sm"
                        className="ml-auto h-8"
                        disabled={isBulkDeleting || isLoading}
                    >
                        {isBulkDeleting ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        {t('chatbot.botDetail.sessions.deleteAll')}
                    </Button>
                )}
            </div>
            
            {/* Content area with fixed height and scrollable */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                {/* Scrollable sessions area */}
                <div className="flex-1 overflow-auto">
                    {isLoading && sessions.length === 0 ? (
                        <div className="p-3 border-b border-gray-200 dark:border-slate-700">
                            <div className="flex justify-between items-center">
                                <div className="flex-1">
                                    <div className="w-2/5 h-6 bg-gray-100 dark:bg-slate-700 rounded mb-1" />
                                    <div className="w-3/5 h-5 bg-gray-100 dark:bg-slate-700 rounded" />
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 h-5 bg-gray-100 dark:bg-slate-700 rounded" />
                                    <div className="w-24 h-5 bg-gray-100 dark:bg-slate-700 rounded" />
                                    <div className="w-20 h-6 bg-gray-100 dark:bg-slate-700 rounded" />
                                </div>
                            </div>
                        </div>
                    ) : sessions.length === 0 && itemsLoaded ? (
                        <EmptyState 
                            title={t('chatbot.botDetail.sessions.emptyTitle')}
                            message={getEmptyMessage()}
                        />
                    ) : (
                        <>
                            {/* Sessions list */}
                            {sessions.map(session => (
                                <SessionRow key={session._id} sessionId={session._id ?? ''} />
                            ))}
                            
                            {/* Intersection observer target for infinite scroll */}
                            <div ref={observerTarget} className="h-4 w-full" />
                            
                            {/* Loading indicator for pagination */}
                            {isLoading && sessions.length > 0 && (
                                <div className="p-4 flex justify-center">
                                    <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-slate-500" />
                                </div>
                            )}
                        </>
                    )}
                </div>
                
                {/* Pagination controls - fixed position at the bottom */}
                <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between" style={{ backgroundColor: 'var(--bot-detail-bg, var(--color-bg))' }}>
                    <div className="text-sm text-gray-500 dark:text-slate-400">
                        {totalSessions > 0 ? (
                            t('chatbot.botDetail.sessions.pagination.showing', {
                                from: Math.min((currentPage - 1) * SESSIONS_PER_PAGE + 1, totalSessions),
                                to: Math.min(currentPage * SESSIONS_PER_PAGE, totalSessions),
                                total: totalSessions,
                            })
                        ) : (
                            t('chatbot.botDetail.sessions.pagination.noSessions')
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={goToPreviousPage} 
                            disabled={currentPage <= 1 || isLoading || totalSessions === 0}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="text-sm">
                            {t('chatbot.botDetail.sessions.pagination.page', { current: currentPage, total: totalPages || 1 })}
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={goToNextPage} 
                            disabled={currentPage >= totalPages || isLoading || totalSessions === 0}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
                <DialogContent className="z-[1201]">
                    <DialogHeader>
                        <DialogTitle>{t('chatbot.botDetail.sessions.deleteAllDialogTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('chatbot.botDetail.sessions.deleteAllDialogDescription', { count: totalSessions })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteAllDialogOpen(false)} disabled={isBulkDeleting}>
                            {t('chatbot.botDetail.common.cancel')}
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteAllConfirm} disabled={isBulkDeleting}>
                            {isBulkDeleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                            {t('chatbot.botDetail.sessions.deleteAll')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
